import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import {OrbitControls, Environment} from '@react-three/drei'
import GarazScianyDach from '../Models/Garaz-sciany-dach'
import DrzwiFramuga from '../Models/Drzwi-framuga'
import Plyta from '../Models/Yta'
import { useSelector } from 'react-redux'
import {
  selectColors,
  selectDoors,
  selectGates,
  selectWindows,
} from '../../containers/Construction/construction.reducer'
import DoorSingle from '../Models/Door-Single'
import CollisionDetector from '../CollisionDetector/CollisionDetector'
import BramaUchylna from '../Models/Brama-uchylna'
import WindowPcv from '../Models/Window-pcv'
import * as THREE from 'three'
import { TexturesLoader } from '../../utils/getTextures'
import GarazSpadWPrzod from '../Models/Garaz-spad-w-przod'
import GarazSpadWLewo from '../Models/Garaz-spad-w-lewo'
import GarazSpadWPrawo from '../Models/Garaz-spad-w-prawo'
import GarazDwuspadowy from '../Models/Garaz-dwuspadowy'
import BramaSegmentowa from '../Models/Brama-segmentowa'
import OtworOkiennyZwykly from '../Models/Otwor-okienny-zwykly'
import GarazDwuspadowyNaBrame from '../Models/Garaz-dwuspadowy-na-brame'
import SwietlikPlexi from '../Models/Swietlik'
import OtworOkiennyKlapaPlexi from '../Models/Otwor-okienny-klapa-plexi'
import OtworOkiennyKlapa from '../Models/Otwor-okienny-klapa'
import BramaRolowana from '../Models/Brama-rolowana'
import Roofing from '../Models/Roofing'
import Tooling from '../Models/Tooling'
import Reinforcements from '../Models/Reinforcments'
import Gutters from '../Models/Gutters'
import BramaHoleGate from "../Models/BramaHoleGate";
import DachRombek from "../Models/DachRombek";


// *** NOWY KOMPONENT DO WYŚWIETLANIA MAPY CIENI ***
function ShadowMapHelper({ lightRef }) {
  const helperRef = useRef()
  const { scene } = useThree()

  useEffect(() => {
    if (lightRef.current) {
      helperRef.current = new THREE.CameraHelper(lightRef.current.shadow.camera)
      scene.add(helperRef.current)

      return () => {
        scene.remove(helperRef.current)
        helperRef.current.dispose()
      }
    }
  }, [lightRef, scene])

  useFrame(() => {
    if (helperRef.current) helperRef.current.update()
  })

  return null
}

function EnableShadowsForScene() {
  const { scene } = useThree()

  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })
  }, [scene])

  return null
}

// ============================
// ✅ TOOLING DATA CLONE (żeby nie mutowało się po drodze)
// ============================
function cloneV3(p) {
  if (!p) return new THREE.Vector3(0, 0, 0)
  if (p.isVector3) return p.clone()
  return new THREE.Vector3(p.x, p.y, p.z)
}

function cloneToolingData(src) {
  if (!src) return null

  return {
    ...src,

    roofPitchRadians: typeof src.roofPitchRadians === "number" ? src.roofPitchRadians : null,

    roofHighPoints: Array.isArray(src.roofHighPoints)
      ? src.roofHighPoints.map(cloneV3)
      : [],

    roofEdges: Array.isArray(src.roofEdges)
      ? src.roofEdges.map((e) => ({ ...e, a: cloneV3(e.a), b: cloneV3(e.b) }))
      : [],

    corners: Array.isArray(src.corners)
      ? src.corners.map((c) => ({ ...c, a: cloneV3(c.a), b: cloneV3(c.b) }))
      : [],

    roofTriangles: Array.isArray(src.roofTriangles)
      ? src.roofTriangles.map((t) => ({
        ...t,
        a: cloneV3(t.a),
        b: cloneV3(t.b),
        c: cloneV3(t.c),
      }))
      : [],
  }
}

export default function GarageScene(props) {
  const [doorsData, setDoorsData] = useState([])
  const [gatesData, setGatesData] = useState([])
  const [windowsData, setWindowsData] = useState([])
  const [texturesData, setTexturesData] = useState(null)
  const [toolingData, setToolingData] = useState(null)

  const gatesReducer = useSelector(selectGates)
  const doorsReducer = useSelector(selectDoors)
  const windowsReducer = useSelector(selectWindows)
  const colorsReducer = useSelector(selectColors)

  const handleTexturesLoaded = useCallback((data) => {
    setTexturesData(data)
  }, [])

  // ✅ stabilny callback na trims/toolingData
  const handleTrimsData = useCallback((data) => {
    setToolingData((prev) => {
      const cloned = cloneToolingData(data)
      if (!cloned) return null

      // zawsze nowa wersja, żeby downstream (Tooling/Reinforcements/Gutters) na pewno się przeliczyły
      cloned.__v = (prev?.__v ?? 0) + 1
      return cloned
    })
  }, [])

  // --- refs ---
  const gateRefs = useRef([])
  const doorRefs = useRef([])
  const windowsRefs = useRef([])

  const gateUpdateFnsRef = useRef([])
  const gateRefFnsRef = useRef([])

  const getGateRefSetter = useCallback((i) => {
    if (!gateRefFnsRef.current[i]) {
      gateRefFnsRef.current[i] = (el) => {
        gateRefs.current[i] = el
      }
    }
    return gateRefFnsRef.current[i]
  }, [])

  const getGateOnUpdate = useCallback((i) => {
    if (!gateUpdateFnsRef.current[i]) {
      gateUpdateFnsRef.current[i] = (newGateData) => {
        setGatesData((prev) => {
          const current = prev[i]
          if (!current) return prev
          if (!newGateData || typeof newGateData !== 'object') return prev

          if (current.closedBoundingBox && current.positionAdjusted) {
            if (newGateData.closedBoundingBox || newGateData.positionAdjusted) return prev
          }

          const nextItem = {
            ...current,
            ...newGateData,
            closedBoundingBox: newGateData.closedBoundingBox || current.closedBoundingBox,
          }

          const noChange =
            nextItem.positionAdjusted === current.positionAdjusted &&
            nextItem.closedBoundingBox === current.closedBoundingBox &&
            nextItem.boundingData === current.boundingData

          if (noChange) return prev

          const next = [...prev]
          next[i] = nextItem
          return next
        })
      }
    }
    return gateUpdateFnsRef.current[i]
  }, [])


  // ============================
  // DATA: gates/windows/doors
  // ============================
  useEffect(() => {
    const newGates = gatesReducer.gates.map((gate) => {
      if (gate.type === 'swing_gate') {
        return {
          type: gate.type,
          position: {
            x: -props.length / 100 / 2 + gate.height / 100 / 2 - 0.06,
            y: 0,
            z: gate.width / 2 / 100 - props.width / 2 / 100 + gate.position / 100,
          },
          width: gate.width,
          height: gate.height - 13,
          boundingData: null,
          garageScale: { x: 1, y: 1, z: 1 },
          wall: 'front_wall',
          id: gate.id,
        }
      } else if (gate.type === 'double_leaf_gate') {
        return {
          type: gate.type,
          position: {
            x: -props.length / 100 / 2 + 0.492,
            y: 0,
            z: gate.width / 2 / 100 - props.width / 2 / 100 + gate.position / 100,
          },
          width: gate.width,
          height: gate.height - 13,
          boundingData: null,
          garageScale: { x: 1, y: 1, z: 1 },
          wall: 'front_wall',
          id: gate.id,
        }
      } else if (gate.type === 'sectional_gate') {
        return {
          type: gate.type,
          position: {
            x: -props.length / 100 / 2 + gate.height / 100 / 2,
            y: 0,
            z: gate.width / 2 / 100 - props.width / 2 / 100 + gate.position / 100,
          },
          width: gate.width,
          height: gate.height,
          boundingData: null,
          garageScale: { x: 1, y: 1, z: 1 },
          wall: 'front_wall',
          id: gate.id,
        }
      } else if (gate.type === 'roller_gate') {
        return {
          type: gate.type,
          position: {
            x: -props.length / 100 / 2 + gate.height / 100 / 2,
            y: 0,
            z: gate.width / 2 / 100 - props.width / 2 / 100 + gate.position / 100,
          },
          width: gate.width,
          height: gate.height,
          boundingData: null,
          garageScale: { x: 1, y: 1, z: 1 },
          wall: 'front_wall',
          id: gate.id,
        }
      } else if (gate.type === 'hole_gate') {
        return {
          type: gate.type,
          position: {
            x: -props.length / 100 / 2,
            y: 0,
            z: gate.width / 2 / 100 - props.width / 2 / 100 + gate.position / 100,
          },
          width: gate.width,
          height: gate.height,
          boundingData: null,
          garageScale: { x: 1, y: 1, z: 1 },
          wall: 'front_wall',
          id: gate.id,
        }
      }
      return null
    })
    setGatesData(newGates)
  }, [gatesReducer, props.width, props.length])

  useEffect(() => {
    const newWindows = {}

    Object.entries(windowsReducer).forEach(([wallKey, wallData]) => {
      newWindows[wallKey] = {}

      ;['openings', 'openings_flaps', 'pcv', 'plexi'].forEach((windowType) => {
        newWindows[wallKey][windowType] = wallData[windowType].map((window) => {
          if (wallKey === 'front_wall' || wallKey === 'back_wall') {
            return {
              position: {
                x: -props.length / 100 / 2,
                y: window.positionVertical / 100 + window.height / 100 / 2,
                z:
                  -props.width / 100 / 2 +
                  window.positionHorizontal / 100 +
                  window.width / 100 / 2,
              },
              width: window.width,
              height: window.height,
              boundingData: null,
              wall: wallKey,
              type: windowType,
              material: window.materialId ?? null,
            }
          } else {
            return {
              position: {
                x:
                  -props.length / 100 / 2 +
                  window.positionHorizontal / 100 +
                  window.width / 100 / 2,
                y: window.positionVertical / 100 + window.height / 100 / 2,
                z: -props.width / 100 / 2,
              },
              width: window.width,
              height: window.height,
              boundingData: null,
              wall: wallKey,
              type: windowType,
              material: window.materialId ?? null,
            }
          }
        })
      })
    })

    setWindowsData(newWindows)
  }, [windowsReducer, props.width, props.length, props.height])

  useEffect(() => {
    const newDoors = {}
    Object.entries(doorsReducer).forEach(([wallKey, wallData]) => {
      newDoors[wallKey] = wallData.doors.map((door) => {
        console.log(door);
        if (wallKey === 'front_wall' || wallKey === 'back_wall') {
          return {
            position: {
              x: -props.length / 100 / 2 + 0.5,
              y: 0,
              z: -props.width / 2 / 100 + door.position / 100 + 200 / 100 / 2 / 2,
            },
            width: door.size,
            height: door.height,
            boundingData: null,
            opening: door.opening,
            gateId: door.gateId,
            wall: wallKey,
            gateSide: door.gateSide,
          }
        } else {
          return {
            position: {
              x: -props.length / 2 / 100 + door.position / 100 + 200 / 100 / 2 / 2,
              y: 0,
              z: (-props.width + 100) / 100 / 2,
            },
            width: door.size,
            height: door.height,
            scale: null,
            wall: wallKey,
            opening: door.opening,
            gateId: door.gateId,
            gateSide: door.gateSide,
          }
        }
      })
    })
    setDoorsData(newDoors)
  }, [doorsReducer, props.width, props.length])

  const getDoorRefIndex = (wallKey, index) => {
    const wallOrder = ['front_wall', 'back_wall', 'left_wall', 'right_wall']
    const refOffset = wallOrder
      .slice(0, wallOrder.indexOf(wallKey))
      .reduce((acc, key) => acc + (doorsData[key]?.length || 0), 0)
    return refOffset + index
  }

  const getWindowRefIndex = (wallKey, type, index) => {
    const wallOrder = ['front_wall', 'back_wall', 'left_wall', 'right_wall']
    const typeOrder = ['openings', 'openings_flaps', 'pcv', 'plexi']

    const wallOffset = wallOrder
      .slice(0, wallOrder.indexOf(wallKey))
      .reduce((acc, key) => {
        return acc + typeOrder.reduce((sum, t) => sum + (windowsData[key]?.[t]?.length || 0), 0)
      }, 0)

    const typeOffset = typeOrder
      .slice(0, typeOrder.indexOf(type))
      .reduce((acc, t) => acc + (windowsData[wallKey]?.[t]?.length || 0), 0)

    return wallOffset + typeOffset + index
  }

  const directionalLightRef = useRef()

  const [gatesResetKey, setGatesResetKey] = useState(0)

  // wszystko co realnie przebudowuje garaż / scenę
  useEffect(() => {
    setGatesResetKey((v) => v + 1)

    // opcjonalnie: wyczyść refy/funcy, żeby nie trzymać starych instancji
    gateRefs.current = []
    gateRefFnsRef.current = []
    gateUpdateFnsRef.current = []
  }, [
    props.activeConstruction,
    props.width,
    props.length,
    props.height,
    props.second_height,
    props.tiles,
    props.structure,
    toolingData?.__v,          // trims/tooling update
    gatesReducer,              // zmiana konfiguracji bram
    doorsReducer,              // jeśli przebudowuje bryłę
    windowsReducer,            // jeśli przebudowuje bryłę
    colorsReducer,             // jeśli zmiana kolorów robi rerender/replace
  ])

  return (
    <Canvas
      shadows
      camera={{ position: [-10, 3, 4], fov: 55 }}
      onCreated={({ gl }) => {
        gl.shadowMap.enabled = true
        gl.shadowMap.type = THREE.PCFSoftShadowMap
      }}
    >
      <CollisionDetector
        gateRefs={gateRefs}
        gatesData={gatesData}
        doorRefs={doorRefs}
        doorsData={doorsData}
        length={props.length}
        width={props.width}
        windowsData={windowsData}
      />

      <directionalLight
        ref={directionalLightRef}
        position={[-50, 15, 30]}
        // castShadow
        intensity={8}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0001}
        shadow-camera-near={0.5}
        shadow-camera-far={500}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
      />

      <Environment
        files="/hdri/horn-koppe_snow_4k.hdr"
        background
        ground={{ height: 4, radius: 35, scale: 80 }}
      />

      <EnableShadowsForScene />

      {props.activeConstruction === 1 && (
        <GarazScianyDach
          key={`garage-1-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      {props.activeConstruction === 2 && (
        <GarazDwuspadowy
          key={`garage-2-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      {props.activeConstruction === 3 && (
        <GarazSpadWLewo
          key={`garage-3-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      {props.activeConstruction === 4 && (
        <GarazSpadWPrawo
          key={`garage-4-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      {props.activeConstruction === 5 && (
        <GarazSpadWPrzod
          key={`garage-5-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      {props.activeConstruction === 6 && (
        <GarazDwuspadowyNaBrame
          key={`garage-6-${props.width}-${props.length}-${props.height}`}
          {...props}
          position={[0, 0, 0]}
          gatesData={gatesData}
          doorsData={doorsData}
          windowsData={windowsData}
          textures={texturesData}
          onTrimsData={handleTrimsData}
        />
      )}

      <Roofing
        activeConstruction={props.activeConstruction}
        textures={texturesData}
        length={props.length}
        width={props.width}
        height={props.height}
        position={[0, 0, 0]}
      />

      <Plyta {...props} />

      {props.isTooling && (
        <Tooling
          key={`tooling-${toolingData?.__v ?? 0}`}
          toolingData={toolingData}
          textures={texturesData}
          toolingColor={props.colors.tooling_color}
        />
      )}


      {props.tiles == 3 && (
        <DachRombek toolingData={toolingData}
                    roofType={props.activeConstruction}
                    debug={false}
                    secondHeight={props.second_height}
                    colors={props.colors}
        />
      )}

      <Reinforcements
        key={`reinforcements-${toolingData?.__v ?? 0}-${props.width}-${props.length}`}
        toolingData={toolingData}
        textures={texturesData}
        reinforcementColor={{ hex: '#cfcfcf', metalnessValue: 0.2, roughnessValue: 0.7 }}
        garageWidth={props.width}
        garageLength={props.length}
        doorsData={doorsData}
        gates={gatesReducer}
        windowsData={windowsData}
        activeConstruction={props.activeConstruction}
      />

      {props.structure == 2 && (
        <Gutters
          key={`gutters-${toolingData?.__v ?? 0}`}
          toolingData={toolingData}
          textures={texturesData}
          gutterColor={props.colors.gutters_color}
          enableDrainageCut={true}
          disableRoofSides={true}
        />
      )}

      {gatesData.map((gate, index) => {
        const onUpdate = getGateOnUpdate(index)
        const setRef = getGateRefSetter(index)

        if (gate.type === 'swing_gate') {
          return (
            <BramaUchylna
              key={`gate-${index}-${gatesResetKey}`}
              gate={gate}
              textures={texturesData}
              onUpdate={onUpdate}
              ref={setRef}
              doorsData={doorsData}
              {...props}
            />
          )
        } else if (gate.type === 'double_leaf_gate') {
          return (
            <DrzwiFramuga
              key={`gate-${index}-${gatesResetKey}`}
              gate={gate}
              textures={texturesData}
              onUpdate={onUpdate}
              ref={setRef}
              doorsData={doorsData}
              {...props}
            />
          )
        } else if (gate.type === 'sectional_gate') {
          return (
            <BramaSegmentowa
              key={`gate-${index}-${gatesResetKey}`}
              gate={gate}
              textures={texturesData}
              onUpdate={onUpdate}
              ref={setRef}
              {...props}
            />
          )
        } else if (gate.type === 'roller_gate') {
          return (
            <BramaRolowana
              key={`gate-${index}-${gatesResetKey}`}
              gate={gate}
              textures={texturesData}
              onUpdate={onUpdate}
              ref={setRef}
              {...props}
            />
          )
        } else if (gate.type === 'hole_gate') {
          return (
            <BramaHoleGate
              key={`gate-${index}-${gatesResetKey}`}
              gate={gate}
              textures={texturesData}
              onUpdate={onUpdate}
              ref={setRef}
              {...props}
            />
          )
        }

        return null
      })}

      {Object.entries(doorsData).map(([wallKey, doorsArray]) => {
        if (!doorsArray || doorsArray.length === 0) return null

        return doorsArray
          .filter((door) => !door?.gateId)
          .map((door, index) => {
          const handleDoorUpdate = (newDoorData) => {
            setDoorsData((prev) => {
              const newData = { ...prev }
              const currentWallDoors = [...newData[wallKey]]
              const current = currentWallDoors[index]

              const updatedDoor = {
                ...current,
                ...newDoorData,
                closedBoundingBox: newDoorData.closedBoundingBox ?? current.closedBoundingBox,
                positionAdjusted: newDoorData.positionAdjusted ?? current.positionAdjusted,
              }

              currentWallDoors[index] = updatedDoor
              newData[wallKey] = currentWallDoors
              return newData
            })
          }

          const setRef = (el) => {
            const refIndex = getDoorRefIndex(wallKey, index)
            doorRefs.current[refIndex] = el
          }

          return (
            <DoorSingle
              key={`${wallKey}-${index}`}
              door={door}
              wall={wallKey}
              onUpdate={handleDoorUpdate}
              textures={texturesData}
              ref={setRef}
              {...props}
            />
          )
        })
      })}

      {Object.entries(windowsData).map(([wallKey, wallWindows]) => {
        if (!wallWindows) return null

        return Object.entries(wallWindows).map(([windowType, windowsArray]) => {
          return windowsArray.map((window, index) => {
            const handleWindowsUpdate = (newWindowData) => {
              setWindowsData((prev) => {
                const newData = { ...prev }
                const currentWallWindows = { ...newData[wallKey] }
                const currentTypeWindows = [...currentWallWindows[windowType]]
                const current = currentTypeWindows[index]

                const updatedWindow = {
                  ...current,
                  ...newWindowData,
                  closedBoundingBox: newWindowData.closedBoundingBox ?? current.closedBoundingBox,
                  positionAdjusted: newWindowData.positionAdjusted ?? current.positionAdjusted,
                }

                currentTypeWindows[index] = updatedWindow
                currentWallWindows[windowType] = currentTypeWindows
                newData[wallKey] = currentWallWindows
                return newData
              })
            }

            const setRef = (el) => {
              const refIndex = getWindowRefIndex(wallKey, windowType, index)
              windowsRefs.current[refIndex] = el
            }

            if (windowType === 'pcv') {
              return (
                <WindowPcv
                  key={`${wallKey}-${windowType}-${index}`}
                  window={window}
                  wall={wallKey}
                  onUpdate={handleWindowsUpdate}
                  ref={setRef}
                  {...props}
                />
              )
            } else if (windowType === 'openings') {
              return (
                <OtworOkiennyZwykly
                  key={`${wallKey}-${windowType}-${index}`}
                  window={window}
                  wall={wallKey}
                  onUpdate={handleWindowsUpdate}
                  ref={setRef}
                  {...props}
                />
              )
            } else if (windowType === 'plexi') {
              return (
                <SwietlikPlexi
                  key={`${wallKey}-${windowType}-${index}`}
                  window={window}
                  wall={wallKey}
                  onUpdate={handleWindowsUpdate}
                  ref={setRef}
                  {...props}
                />
              )
            } else if (windowType === 'openings_flaps' && window.material == 44) {
              return (
                <OtworOkiennyKlapa
                  key={`${wallKey}-${windowType}-${index}`}
                  window={window}
                  wall={wallKey}
                  onUpdate={handleWindowsUpdate}
                  ref={setRef}
                  {...props}
                  textures={texturesData}
                />
              )
            } else if (windowType === 'openings_flaps' && window.material == 45) {
              return (
                <OtworOkiennyKlapaPlexi
                  key={`${wallKey}-${windowType}-${index}`}
                  window={window}
                  wall={wallKey}
                  onUpdate={handleWindowsUpdate}
                  ref={setRef}
                  {...props}
                />
              )
            }

            return null
          })
        })
      })}

      <OrbitControls
        target={[0, 1, 0]}
        maxPolarAngle={Math.PI / 2}
        maxDistance={20}
        minDistance={5}
        makeDefault
      />

      {/* <ShadowMapHelper lightRef={directionalLightRef} /> */}

      <TexturesLoader
        onLoad={handleTexturesLoaded}
        colorsReducer={colorsReducer}
        propsColors={props.colors}
      />
    </Canvas>
  )
}
